import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router';
import { AdapterConfigLabelField } from './AdapterConfigLabelField';
import { AdapterServiceKeyField } from './AdapterServiceKeyField';
import { adapterServiceApi } from '../../infrastructure/api/adapterServiceApi';
import { ADAPTER_SERVICES_QUERY_KEY } from '../../hooks/useAdapterServices';

type Props = {
  open: boolean;
  onClose: () => void;
};

export function CreateServiceDialog({ open, onClose }: Props) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [adapterServiceKey, setAdapterServiceKey] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [flowType, setFlowType] = useState<'single' | 'multi_step'>('multi_step');
  const [configLabel, setConfigLabel] = useState('default');
  const [error, setError] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: () =>
      adapterServiceApi.create({
        adapterServiceKey: adapterServiceKey.trim().toLowerCase(),
        displayName: displayName.trim(),
        configLabel: configLabel.trim() || 'default',
        flowType,
        seedFromRegistry: false,
      }),
    onSuccess: async item => {
      await qc.invalidateQueries({ queryKey: ADAPTER_SERVICES_QUERY_KEY });
      onClose();
      void navigate(`/service-onboarding/${item.id}`);
    },
    onError: (e: Error) => setError(e.message),
  });

  const reset = () => {
    setAdapterServiceKey('');
    setDisplayName('');
    setFlowType('multi_step');
    setConfigLabel('default');
    setError(null);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Add service</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <AdapterServiceKeyField value={adapterServiceKey} onChange={setAdapterServiceKey} />
          <TextField
            label="Display name"
            value={displayName}
            onChange={e => setDisplayName(e.target.value)}
            fullWidth
            required
          />
          <AdapterConfigLabelField value={configLabel} onChange={setConfigLabel} />
          <FormControl fullWidth>
            <InputLabel>Flow type</InputLabel>
            <Select label="Flow type" value={flowType} onChange={e => setFlowType(e.target.value as typeof flowType)}>
              <MenuItem value="multi_step">Multi-step (adapter steps)</MenuItem>
              <MenuItem value="single">Single call</MenuItem>
            </Select>
          </FormControl>
          {error && (
            <Typography variant="body2" color="error">
              {error}
            </Typography>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={() => {
            reset();
            onClose();
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={!adapterServiceKey.trim() || !displayName.trim() || create.isPending}
          onClick={() => {
            setError(null);
            create.mutate();
          }}
        >
          {create.isPending ? 'Creating…' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
